import type { ReactNode } from 'react';

import { cn } from '@shared/ui/cn';

/**
 * The table family.
 *
 * Every column aligns to `start`, numbers included; numeric cells carry tabular
 * figures so digits line up; the head is sticky and opaque, because a long table
 * without one is unreadable.
 *
 * **The table scrolls inside its own wrapper and the page body never scrolls
 * horizontally.** The wrapper is also the containing block, so an absolutely
 * positioned descendant — a visually-hidden span, a stray badge — cannot escape
 * the clip and push the document past the viewport.
 *
 * A minimum inline size is what makes that work: a table left at `100%` shrinks
 * to its container instead of overflowing it, the wrapper scrolls nothing, and
 * the label column collapses to one word per line.
 */
export function DTableScroll({ children }: { children: ReactNode }) {
  return (
    <div className="relative overflow-x-auto rounded-md border border-border bg-surface [overscroll-behavior-inline:contain]">
      {children}
    </div>
  );
}

export function DTable({
  caption,
  minInlineSize = '30rem',
  children,
}: {
  /** Visually hidden, and never omitted: it names what the table partitions. */
  caption: string;
  minInlineSize?: string;
  children: ReactNode;
}) {
  return (
    <table
      style={{ minInlineSize }}
      className="w-full border-collapse text-sm [&_tbody_tr:last-child_td]:border-b-0 [&_tbody_tr:last-child_th]:border-b-0 [&_td]:border-b [&_td]:border-border [&_td]:p-3 [&_td]:px-4 [&_th]:border-b [&_th]:border-border [&_th]:p-3 [&_th]:px-4"
    >
      <caption className="sr-only">{caption}</caption>
      {children}
    </table>
  );
}

export function DTableHead({ children }: { children: ReactNode }) {
  return (
    <thead className="[&_th]:sticky [&_th]:z-[1] [&_th]:bg-bg-sunken [&_th]:text-xs [&_th]:font-semibold [&_th]:text-ink-muted [&_th]:[inset-block-start:0]">
      {children}
    </thead>
  );
}

/**
 * The rows.
 *
 * **The hover is the accent's soft fill, not the neutral wash.** Every table in
 * this console is a work queue whose rows are opened one at a time, and
 * `--hover-bg` is a 7 % ink tint — on the dark theme it is very nearly the row's
 * own ground, so the pointer told the operator nothing about which row it was
 * over. `--accent-soft` is the same brand red the rest of the console is built
 * from at 14 %, which reads at a glance without competing with a state capsule
 * sitting in the row.
 *
 * It is a **hover**, never a state: it says where the pointer is and nothing
 * about the row, so it carries no meaning a keyboard reader or a screen reader
 * loses. The focus ring is still what marks the keyboard's position.
 */
export function DTableBody({ children }: { children: ReactNode }) {
  return <tbody className="[&_tr:hover]:bg-accent-soft">{children}</tbody>;
}

/**
 * The total row. A total is neither a state nor an action, so it takes weight
 * and a stronger rule and **no colour** — a colour token here would imply one.
 */
export function DTableFoot({ children }: { children: ReactNode }) {
  return (
    <tfoot className="[&_td]:border-b-0 [&_td]:border-t [&_td]:border-t-border-strong [&_td]:bg-bg-sunken [&_td]:font-semibold [&_th]:border-b-0 [&_th]:border-t [&_th]:border-t-border-strong [&_th]:bg-bg-sunken [&_th]:font-semibold">
      {children}
    </tfoot>
  );
}

/** The cell that names its row — body weight, never head weight. */
export function DTableRowHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th scope="row" className={cn('font-regular text-ink', className)}>
      {children}
    </th>
  );
}
